import { CloseOutlined, DeleteOutlined, LoadingOutlined, MessageOutlined } from "@ant-design/icons";
import { useState } from "react";
import type { ConversationSummary } from "./conversationApi";

interface ConversationHistoryProps {
    conversations: ConversationSummary[];
    loading: boolean;
    activeConversationId: string | null;
    onSelect: (conversationId: string) => void;
    onDelete: (conversationId: string) => Promise<void> | void;
    onClose: () => void;
}

function formatDate(iso?: string): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function ConversationHistory({ conversations, loading, activeConversationId, onSelect, onDelete, onClose }: ConversationHistoryProps) {
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleDelete = async (e: React.MouseEvent, conversationId: string) => {
        e.stopPropagation(); // don't trigger the row's onSelect
        setDeletingId(conversationId);
        try {
            await onDelete(conversationId);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="absolute inset-0 z-10 flex flex-col bg-[#fafafa]">
            <div className="flex flex-none items-center justify-between border-b border-zinc-200 px-4 py-3">
                <span className="text-sm font-semibold text-[#001D3D]">Conversation history</span>
                <button
                    type="button"
                    onClick={onClose}
                    title="Close history"
                    className="rounded-full p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                >
                    <CloseOutlined />
                </button>
            </div>

            <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
                {loading && (
                    <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500">
                        <LoadingOutlined spin /> Loading conversations...
                    </div>
                )}

                {!loading && conversations.length === 0 && (
                    <div className="py-8 text-center text-sm text-zinc-500">No past conversations for this data set yet.</div>
                )}

                {!loading &&
                    conversations.map((c) => {
                        const isActive = c.conversationId === activeConversationId;
                        const isDeleting = c.conversationId === deletingId;
                        return (
                            <div
                                key={c.conversationId}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelect(c.conversationId)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") onSelect(c.conversationId);
                                }}
                                className={`group flex w-full cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left transition-colors ${
                                    isActive
                                        ? "border-[#001D3D] bg-white"
                                        : "border-zinc-200 bg-white hover:border-[#001D3D]/40 hover:bg-zinc-50"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="flex items-center gap-2 truncate text-sm font-medium text-zinc-900">
                                        <MessageOutlined className="text-[#001D3D]" />
                                        <span className="truncate">{c.title || "Conversation"}</span>
                                        {isActive && <span className="flex-none rounded bg-[#001D3D] px-1.5 py-0.5 text-[10px] font-normal text-white">Current</span>}
                                    </span>
                                    <div className="flex flex-none items-center gap-2">
                                        <span className="text-[10px] text-zinc-400">{formatDate(c.updatedDate || c.createdDate)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleDelete(e, c.conversationId)}
                                            disabled={isDeleting}
                                            title="Delete conversation"
                                            className="rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 disabled:opacity-50"
                                        >
                                            {isDeleting ? <LoadingOutlined spin /> : <DeleteOutlined />}
                                        </button>
                                    </div>
                                </div>
                                {c.lastMessagePreview && <div className="truncate text-xs text-zinc-500">{c.lastMessagePreview}</div>}
                                <div className="text-[10px] text-zinc-400">
                                    {c.messageCount || 0} message{(c.messageCount || 0) === 1 ? "" : "s"}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}